import React, { useState, useImperativeHandle, useEffect } from "react";
import { useFormik, FormikProvider, FieldArray } from "formik";
import moment from "moment";

// components
import InputField from "../../../components/forms/InputField/InputField.component";
import FormSectionHeader from "../../../components/forms/FormSectionHeader/FormSectionHeader.component";
import ActionButton from "../../../components/buttons/ActionButton/ActionButton.component";
import AsyncSelectField from "../../../components/forms/AsyncSelectField/AsyncSelectField.component";
import CrewAssignment from "../../../components/forms/lists/CrewAssignment/CrewAssignment.component";
import SelectField from "../../../components/forms/SelectField/SelectField.component"; // Add this import

// queries
import { GET_ALL_JOBS_FOR_SELECT_FIELD } from "../../../graphql/job/job.queries";
import { GET_ALL_CREW_MEMBERS_FOR_SELECT_FIELD } from "../../../graphql/crewMember/crewMember.queries";
import { GET_ALL_TRUCKS_FOR_SELECT_FIELD } from "../../../graphql/truck/truck.queries";

// mui
import { Grid, MenuItem, TextField } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const VEHICLE_OPTIONS = [
  { value: "", label: "Select Vehicle Option" },
  { value: "NO_VEHICLE", label: "No Vehicle" },
  { value: "PERSONAL_VEHICLE", label: "Personal Vehicle" },
];

// Use code keys that match what's actually stored: J,Y,1..8,F,S,A,C
const CREW_MEMBER_CLASS_LABEL = {
  J: "Journeyman",
  Y: "Yard",
  1: "A1",
  2: "A2",
  3: "A3",
  4: "A4",
  5: "A5",
  6: "A6",
  7: "A7",
  8: "A8",
  F: "Foreman",
  S: "Superintendent",
  A: "Admin",
  C: "Clerk",
};

const formatErrors = (errs) => {
  if (!errs || typeof errs !== "object") return "";
  if (errs.__server) return String(errs.__server);

  const lines = [];
  for (const [k, v] of Object.entries(errs)) {
    if (k === "emergencyContacts" || k === "certifications") {
      if (Array.isArray(v)) {
        v.forEach((itemErr, idx) => {
          if (itemErr && typeof itemErr === "object") {
            for (const [ik, iv] of Object.entries(itemErr)) {
              if (iv)
                lines.push(
                  `${k}[${idx}].${ik}: ${Array.isArray(iv) ? iv[0] : String(iv)}`
                );
            }
          }
        });
      }
    } else if (v) {
      lines.push(`${k}: ${Array.isArray(v) ? v[0] : String(v)}`);
    }
  }
  return lines.join("\n");
};

const AddJobDay = React.forwardRef(({ errors = {}, status = "idle" }, ref) => {
  const [selectedCrewMember, setSelectedCrewMember] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const isBusy = status === "loading" || status === "saving";
  const busyText = status === "loading" ? "Loading…" : status === "saving" ? "Saving…" : "";

  useEffect(() => {
    const msg = formatErrors(errors);
    if (msg) {
      setToastMsg(msg);
      setToastOpen(true);
    }
  }, [errors]);

  const formik = useFormik({
    initialValues: {
      job: "",
      truck: "",
      driver: "",
      truck2: "",
      driver2: "",
      truck3: "",
      driver3: "",
      scopeOfWork: "",
      date: moment(),
      startTime: moment(`1/1/2023 07:00 AM`),
      endTime: null,
      crewAssignments: [],
      crewMemberSearch: "",
      vehicleOption: "", // NEW FIELD ADDED
    },
    onSubmit: (values) => {
      // No-op here; submission handled by controller/modal
    },
  });

  useImperativeHandle(ref, () => ({
    getData: () => formik.values,
  }));

  const handleCrewSelect = (opt) => {
    if (!opt || !opt.value || !opt.data) {
      setSelectedCrewMember(null);
      formik.setFieldValue("crewMemberSearch", "");
      return;
    }
    const cm = opt.data;
    const idNum = Number.parseInt(opt.value, 10);

    setSelectedCrewMember({
      id: Number.isNaN(idNum) ? null : idNum,
      firstName: cm.firstName || "",
      lastName: cm.lastName || "",
      classification: String(cm.classification || "")
        .replace(/^EnumMeta\./, "")
        .toUpperCase(),
    });
    formik.setFieldValue("crewMemberSearch", opt.value);
  };

  return (
    <div style={{ position: "relative" }}>
      {isBusy && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            fontWeight: 600,
          }}
          aria-busy
          aria-live="polite"
        >
          {busyText}
        </div>
      )}

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} aria-disabled={isBusy}>
          <FormSectionHeader text="Job Info" />
          
          <AsyncSelectField
            id="job"
            value={formik.values.job}
            placeholder="Job"
            onChange={(value) => formik.setFieldValue("job", value.value)}
            query={{
              query: GET_ALL_JOBS_FOR_SELECT_FIELD,
              rules: { loopField: "allJobs", label: ["name"], value: "id" },
            }}
            isValid={errors?.job}
            disabled={isBusy}
          />

          <InputField
            id="scopeOfWork"
            placeholder="Scope"
            type="text"
            onChange={formik.handleChange}
            value={formik.values.scopeOfWork}
            required={false}
            isValid={errors?.scopeOfWork}
            disabled={isBusy}
          />

          <InputField
            id="date"
            placeholder="Date"
            type="date"
            onChange={(value) => formik.setFieldValue("date", value)}
            value={formik.values.date}
            isValid={errors?.date}
            disabled={isBusy}
          />

          <InputField
            id="startTime"
            placeholder="Start Time"
            type="time"
            onChange={(value) => formik.setFieldValue("startTime", value)}
            value={formik.values.startTime}
            isValid={errors?.startTime}
            disabled={isBusy}
          />

          <InputField
            id="endTime"
            placeholder="End Time"
            type="time"
            onChange={(value) => formik.setFieldValue("endTime", value)}
            value={formik.values.endTime}
            isValid={errors?.endTime}
            disabled={isBusy}
          />

          {/* NEW VEHICLE OPTION DROPDOWN */}
          <FormSectionHeader text="Vehicle Option" />
          
          <TextField
            id="vehicleOption"
            name="vehicleOption"
            select
            label="Vehicle Option"
            value={formik.values.vehicleOption}
            onChange={formik.handleChange}
            fullWidth
            disabled={isBusy}
            error={Boolean(errors?.vehicleOption)}
            helperText={errors?.vehicleOption}
            sx={{ marginBottom: 2 }}
          >
            {VEHICLE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <FormSectionHeader text="Trucks" />

          <AsyncSelectField
            id="truck"
            value={formik.values.truck}
            placeholder="Truck #1"
            onChange={(value) => formik.setFieldValue("truck", value.value)}
            query={{
              query: GET_ALL_TRUCKS_FOR_SELECT_FIELD,
              rules: { loopField: "allTrucks", label: ["number"], value: "id" },
            }}
            required={false}
            isValid={errors?.truck}
            disabled={isBusy}
          />

          <AsyncSelectField
            id="driver"
            value={formik.values.driver}
            placeholder="Driver #1"
            onChange={(value) => formik.setFieldValue("driver", value.value)}
            query={{
              query: GET_ALL_CREW_MEMBERS_FOR_SELECT_FIELD,
              rules: {
                loopField: "allCrewMembers",
                label: ["firstName", "lastName"],
                value: "id",
                filterField: "isActive",
                filterValue: true,
              },
            }}
            required={false}
            isValid={errors?.driver}
            disabled={isBusy}
          />

          <AsyncSelectField
            id="truck2"
            value={formik.values.truck2}
            placeholder="Truck #2"
            onChange={(value) => formik.setFieldValue("truck2", value.value)}
            query={{
              query: GET_ALL_TRUCKS_FOR_SELECT_FIELD,
              rules: { loopField: "allTrucks", label: ["number"], value: "id" },
            }}
            required={false}
            isValid={errors?.truck2}
            disabled={isBusy}
          />

          <AsyncSelectField
            id="driver2"
            value={formik.values.driver2}
            placeholder="Driver #2"
            onChange={(value) => formik.setFieldValue("driver2", value.value)}
            query={{
              query: GET_ALL_CREW_MEMBERS_FOR_SELECT_FIELD,
              rules: {
                loopField: "allCrewMembers",
                label: ["firstName", "lastName"],
                value: "id",
                filterField: "isActive",
                filterValue: true,
              },
            }}
            required={false}
            isValid={errors?.driver2}
            disabled={isBusy}
          />

          <AsyncSelectField
            id="truck3"
            value={formik.values.truck3}
            placeholder="Truck #3"
            onChange={(value) => formik.setFieldValue("truck3", value.value)}
            query={{
              query: GET_ALL_TRUCKS_FOR_SELECT_FIELD,
              rules: { loopField: "allTrucks", label: ["number"], value: "id" },
            }}
            required={false}
            isValid={errors?.truck3}
            disabled={isBusy}
          />

          <AsyncSelectField
            id="driver3"
            value={formik.values.driver3}
            placeholder="Driver #3"
            onChange={(value) => formik.setFieldValue("driver3", value.value)}
            query={{
              query: GET_ALL_CREW_MEMBERS_FOR_SELECT_FIELD,
              rules: {
                loopField: "allCrewMembers",
                label: ["firstName", "lastName"],
                value: "id",
                filterField: "isActive",
                filterValue: true,
              },
            }}
            required={false}
            isValid={errors?.driver3}
            disabled={isBusy}
          />

          <FieldArray
            name="crewAssignments"
            render={({ remove, push }) => (
              <div>
                <FormSectionHeader text="Crew Assignments" />
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={10}>
                    <AsyncSelectField
                      id="crewMemberSearch"
                      value={formik.values.crewMemberSearch}
                      placeholder="Search"
                      onChange={handleCrewSelect}
                      query={{
                        query: GET_ALL_CREW_MEMBERS_FOR_SELECT_FIELD,
                        rules: {
                          loopField: "allCrewMembers",
                          label: ["firstName", "lastName"],
                          value: "id",
                          filterField: "isActive",
                          filterValue: true,
                        },
                      }}
                      required={false}
                      disabled={isBusy}
                    />
                  </Grid>
                  <Grid container item xs={2}>
                    <ActionButton
                      sx={{ width: "100%", marginBottom: 2 }}
                      label="Add"
                      disabled={
                        isBusy ||
                        !selectedCrewMember ||
                        selectedCrewMember.id == null
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        push(selectedCrewMember);
                        formik.setFieldValue("crewMemberSearch", "");
                        setSelectedCrewMember(null);
                      }}
                    />
                  </Grid>
                </Grid>

                {formik.values.crewAssignments.map((assignment, index) => (
                  <CrewAssignment
                    key={assignment.id ?? index}
                    variant="job-day"
                    id={index}
                    data={{
                      title: `${assignment.firstName} ${assignment.lastName}`,
                      subTitle:
                        CREW_MEMBER_CLASS_LABEL[assignment.classification] ||
                        assignment.classification,
                    }}
                    onRemoveAction={(e) => {
                      e.preventDefault();
                      if (isBusy) return;
                      remove(index);
                    }}
                  />
                ))}
              </div>
            )}
          />
        </form>
      </FormikProvider>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </div>
  );
});

export default AddJobDay;
